'use client';

import { Icon } from '@/components/Icon';
import { externalProps } from '@/components/menu/menuUtils';
import { useShellState } from '@/lib/useShellState';
import type { MenuTree } from '@/types/menu';

interface FooterMenuColumnsProps {
  /**
   * The footer menu the `(main)` layout resolved for the pathname it
   * server-rendered, labels already substituted. Only the *first* page's
   * answer: the layout does not re-run on a client-side navigation, so from
   * there on the labels come from `useShellState` — a footer label may carry a
   * `{location.*}` or `{city.*}` token (70.11), and it resolves against
   * whichever page is rendering.
   */
  initialMenu: MenuTree | null;
  /**
   * The footer renders the same columns twice, once per breakpoint: as a grid
   * from `lg` up, and as collapsible blocks below it. One component so the two
   * cannot drift, and so a navigation refreshes both from one round trip.
   */
  variant: 'desktop' | 'mobile';
}

export const FooterMenuColumns = ({
  initialMenu,
  variant,
}: FooterMenuColumnsProps) => {
  const menu = useShellState(initialMenu, (state) => state.footerMenu);

  const columns = (menu?.items ?? []).map((item) => ({
    title: item.label,
    links: item.children.map((child) => ({
      label: child.label,
      href: child.linkTarget ?? '#',
    })),
  }));

  // A Fragment, deliberately: on desktop these columns are grid items beside
  // the social column, so a wrapper element would become the single grid item.
  if (variant === 'desktop') {
    return (
      <>
        {columns.map((col) => (
          <div key={col.title}>
            <p className="mb-3 font-medium">{col.title}</p>
            <ul className="list-none space-y-2 pl-0">
              {col.links.map((link) => (
                <li key={link.label} className="my-2">
                  <a
                    href={link.href}
                    {...externalProps(link.href)}
                    className="hover:text-primary text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </>
    );
  }

  return (
    <>
      {columns.map((col) => (
        <details key={col.title} className="border-card-border border-t">
          <summary className="text-foreground flex cursor-pointer list-none items-center justify-between py-3 [&::-webkit-details-marker]:hidden">
            <span className="font-base text-sm md:font-medium">
              {col.title}
            </span>
            <Icon
              src="/icons/svg/chevron-down.svg"
              size={16}
              className="shrink-0 transition-transform duration-200 [[open]_&]:rotate-180"
            />
          </summary>
          <ul className="list-none space-y-3 pb-4 pl-0">
            {col.links.map((link) => (
              <li key={link.label} className="my-2">
                <a
                  href={link.href}
                  {...externalProps(link.href)}
                  className="hover:text-primary text-sm"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </>
  );
};
